import clsx from 'clsx';
import { graphql, PageProps, useStaticQuery } from 'gatsby';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Container } from 'components/GridV2';
import Link from 'components/Link';

import { IQueryParams } from 'typescriptDefs';
import { LocaleNavbarTab, NavbarTab } from 'typescriptDefs/layout';

import { trackEvent, useIsTabletSize } from 'helpers';
import { generalAppsFlyerIds } from 'helpers/getAfTracking';
import { localizeNavigationLinks } from 'helpers/getNavigationLinks';
import { TLocale } from 'utils/i18n';

import { DesktopLinks } from './DesktopLinks';
import { DesktopMenu } from './DesktopMenu';
import { LocaleSwitch } from './LocaleSwitch';
import { LoginAndRegistration } from './LoginAndRegistration';
import { MobileHamburgerMenuButton } from './MobileHamburgerMenuButton';
import { MobileMenu } from './MobileMenu';
import { NavbarLogoAndCTA } from './NavbarLogoAndCTA';

type Props = {
  banner?: (props: { showSubheading: boolean }) => JSX.Element;
  className?: string;
  ctaToNavbar?: string;
  darkBg?: boolean;
  navItemsOverride?: { text: string; url: string }[];
  pageProps: {
    locale: TLocale;
    location: PageProps['location'];
    pageIsMultilingual: boolean;
    pageName: string;
    queryParams: IQueryParams;
    variant: string | undefined;
  };
};

const APPROXIMATE_HERO_HEIGHT = 900;

const Navbar = ({ banner, className, ctaToNavbar, darkBg = false, navItemsOverride, pageProps }: Props) => {
  const navigationData = useStaticQuery<INavbarGraphQLData>(queryGraphQL);
  const [openDesktopTab, setOpenDesktopTab] = useState<LocaleNavbarTab | null>(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [secondNavOpen, setSecondNavOpen] = useState(true);
  const [isAtTopOfPage, setIsAtTopOfPage] = useState(true);
  const isTabletSize = useIsTabletSize();

  useEffect(() => {
    trackEvent('navbar_viewed', {
      label: 'Variant',
      category: pageProps.pageName,
      locale: pageProps.locale,
      location: pageProps.variant,
    });
  }, []);

  const localizedNavbarTabs = localizeNavigationLinks({
    tabs: navigationData.navigation.tabs,
    locale: pageProps.locale,
    searchParams: pageProps.location?.search,
  });

  const closeDesktopMenu = () => setOpenDesktopTab(null);

  const trackLinkClicked = (label: string) =>
    trackEvent('navlink_clicked', {
      category: pageProps.pageName,
      label,
      locale: pageProps.locale,
    });

  useEffect(() => {
    window?.addEventListener('scroll', closeDesktopMenu);
    return () => window?.removeEventListener('scroll', closeDesktopMenu);
  }, []);

  useEffect(() => {
    if (!isTabletSize) setIsMobileNavOpen(false);
    if (openDesktopTab !== null && isTabletSize) setOpenDesktopTab(null);
  }, [isTabletSize]);

  useEffect(() => {
    let prevScrollPos = window.scrollY;

    const handleShowOnScroll = () => {
      const currentScrollPos = window.scrollY;
      const userHasScrolledUp = prevScrollPos > currentScrollPos || currentScrollPos < APPROXIMATE_HERO_HEIGHT;

      setSecondNavOpen(userHasScrolledUp);
      setIsAtTopOfPage(currentScrollPos === 0);

      prevScrollPos = currentScrollPos;
    };

    handleShowOnScroll();

    window.addEventListener('scroll', handleShowOnScroll);

    return () => window.removeEventListener('scroll', handleShowOnScroll);
  }, []);

  const handleToggleMenu = (val: boolean) => {
    // Prevent page scrolling when the menu is open
    document.body.style.overflow = val ? 'hidden' : 'auto';
    setIsMobileNavOpen(val);
  };

  return (
    <nav
      className={twMerge(
        clsx(
          'fixed left-0 right-0 z-nav bg-white [box-shadow:0px_4px_4px_#00000003]',
          darkBg && 'bg-primary-500 [box-shadow:0px_4px_4px_#0000001A]',
          className,
        ),
      )}
    >
      <div onMouseLeave={closeDesktopMenu} onMouseEnter={() => !isTabletSize && setSecondNavOpen(true)}>
        <Container
          className={twMerge(
            clsx(
              'relative box-border flex w-full items-center justify-between px-16 xs:p-0',
              'h-navMobileHeight lg:h-navHeight',
              darkBg && 'text-white',
            ),
          )}
        >
          <div className="flex items-center">
            <NavbarLogoAndCTA
              ctaTo={ctaToNavbar}
              darkBg={darkBg}
              location={pageProps.location}
              navOpen={isMobileNavOpen}
              pageName={pageProps.pageName}
              queryParams={pageProps.queryParams}
            />
          </div>
          {navItemsOverride ? (
            <div className="hidden w-fit flex-row gap-4 py-12 lg:flex">
              {navItemsOverride.map((link) => (
                <Link
                  key={link.text}
                  className={twMerge('px-16 text-md font-medium text-grey-500', darkBg && 'text-white')}
                  to={link.url}
                >
                  {link.text}
                </Link>
              ))}
            </div>
          ) : null}
          {isTabletSize ? (
            <div className="flex items-center">
              <LocaleSwitch
                darkBg={darkBg}
                location={pageProps.location}
                pageIsMultilingual={pageProps.pageIsMultilingual}
                className="lg:hidden"
              />

              {navItemsOverride && navItemsOverride.length === 0 ? null : (
                <MobileHamburgerMenuButton open={isMobileNavOpen} handleToggleMenu={handleToggleMenu} darkBg={darkBg} />
              )}
            </div>
          ) : (
            <LoginAndRegistration
              buttonLocation="Navbar desktop"
              darkBg={darkBg}
              trackEventProps={{
                category: pageProps.pageName,
                label: 'Navbar desktop',
                locale: pageProps.locale,
                location: pageProps.variant,
              }}
              queryParams={{
                hash: pageProps.queryParams.hash,
                search: {
                  ...pageProps.queryParams.search,
                  af_adset_id: generalAppsFlyerIds.NAV,
                },
              }}
              isOverrideNav={!!navItemsOverride}
              pageIsMultilingual={pageProps.pageIsMultilingual}
              pageLocation={pageProps.location}
            />
          )}
        </Container>

        {/* desktop dropdown */}
        <div
          className={twMerge(
            clsx(
              'hidden h-0 overflow-hidden transition-all duration-[0.2s] ease-in-out [box-shadow:0px_4px_4px_#00000003] lg:block',
              secondNavOpen && !navItemsOverride && 'h-navSecondaryHeight',
              darkBg && '[box-shadow:0px_4px_4px_#0000001A]',
            ),
          )}
          role="menubar"
        >
          {navItemsOverride ? null : (
            <DesktopLinks
              darkBg={darkBg}
              tabs={localizedNavbarTabs}
              openTab={openDesktopTab}
              setOpenTab={(tab: LocaleNavbarTab | null) => setOpenDesktopTab(tab)}
              location={pageProps.location}
              pageIsMultilingual={pageProps.pageIsMultilingual}
            />
          )}
        </div>

        {openDesktopTab && <DesktopMenu darkBg={darkBg} tab={openDesktopTab} trackLinkClicked={trackLinkClicked} />}

        {/* mobile dropdown */}
        {isMobileNavOpen && (
          <MobileMenu
            darkBg={darkBg}
            tabs={localizedNavbarTabs}
            queryParams={pageProps.queryParams}
            trackEventProps={{
              category: pageProps.pageName,
              label: 'Navbar mobile',
              locale: pageProps.locale,
              location: pageProps.variant,
            }}
            trackLinkClicked={trackLinkClicked}
            navItemsOverride={navItemsOverride}
            closeMobileMenu={() => handleToggleMenu(false)}
          />
        )}
      </div>
      {banner && <div className="fixed z-8 w-full">{banner({ showSubheading: secondNavOpen && !isAtTopOfPage })}</div>}
    </nav>
  );
};

export default Navbar;

interface INavbarGraphQLData {
  navigation: {
    tabs: NavbarTab[];
  };
}

const queryGraphQL = graphql`
  query {
    navigation: sanitySiteNav(name: { eq: "Default" }) {
      tabs {
        enTitle
        frTitle
        content {
          ... on SanitySiteNavSection {
            _type
            enTitle
            frTitle
            enDescription
            frDescription
            alignVertically
            links {
              enTitle
              frTitle
              enUrl
              frUrl
              isNew
            }
          }
          ... on SanitySiteNavigationLink {
            _type
            enTitle
            frTitle
            enDescription
            frDescription
            enUrl
            frUrl
            isNew
            image {
              altText
              img {
                asset {
                  gatsbyImageData(placeholder: NONE, width: 600)
                }
              }
            }
          }
        }
        featured {
          enTitle
          frTitle
          enUrl
          frUrl
          enDescription
          frDescription
          enLinkText
          frLinkText
          image {
            altText
            img {
              asset {
                gatsbyImageData(placeholder: NONE, width: 600)
              }
            }
          }
        }
      }
    }
  }
`;
